import { Fragment, useCallback, useEffect, useState } from 'react';
import { Form, Row } from 'reactstrap';
import { X } from 'react-feather';
import { Link } from 'react-router-dom';
import { useAuth } from '@/core/auth/useAuth';
import { useEntitlementModules } from '@/features/admin/platformHooks';
import { useTheme } from '../ThemeContext';
import {
  filterMenuByModules,
  getMenuForRole,
  type MenuLink,
  type MenuSection,
} from '../menuConfig';
import { SvgIcon } from '../SvgIcon';
import { Leftbar } from './Leftbar';
import { RightHeader } from './RightHeader';

/** CUBA Header — search Typeahead + Leftbar + RightHeader */
export function Header() {
  const { toggleIcon } = useTheme();
  const { role, user } = useAuth();
  const { modules } = useEntitlementModules(role, user?.schoolId);
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState<Array<MenuLink & { icon?: string }>>([]);
  const [empty, setEmpty] = useState(false);

  const flattenMenu = useCallback(() => {
    const items: Array<MenuLink & { icon?: string }> = [];
    filterMenuByModules(getMenuForRole(role), modules).forEach((group) => {
      group.Items.forEach((section: MenuSection) => {
        if (section.type === 'link' && section.path) {
          items.push({ title: section.title, path: section.path, type: 'link', icon: section.icon });
        }
        section.children?.forEach((child) => {
          items.push({ ...child, icon: section.icon });
        });
      });
    });
    return items;
  }, [role, modules]);

  const handleSearch = (keyword: string) => {
    setSearchValue(keyword);
    const menu = document.querySelector('.Typeahead-menu');
    const emptyMenu = document.querySelector('.empty-menu');
    if (!keyword) {
      menu?.classList.remove('is-open');
      emptyMenu?.classList.remove('is-open');
      setResults([]);
      setEmpty(false);
      return;
    }
    const hits = flattenMenu().filter((m) =>
      m.title.toLowerCase().includes(keyword.toLowerCase()),
    );
    setResults(hits);
    setEmpty(hits.length === 0);
    menu?.classList.toggle('is-open', hits.length > 0);
    emptyMenu?.classList.toggle('is-open', hits.length === 0);
  };

  const removeFix = () => {
    setSearchValue('');
    setResults([]);
    document.querySelector('.Typeahead-menu')?.classList.remove('is-open');
    document.querySelector('.empty-menu')?.classList.remove('is-open');
    document.querySelector('.search-full')?.classList.remove('open');
  };

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') removeFix();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, []);

  return (
    <Fragment>
      <div className={`page-header ${toggleIcon ? 'close_icon' : ''}`}>
        <Row className="header-wrapper m-0">
          <Form
            className="form-inline search-full col"
            action="#"
            method="get"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="form-group w-100">
              <div className="Typeahead Typeahead--twitterUsers">
                <div className="u-posRelative">
                  <input
                    className="Typeahead-input form-control-plaintext w-100"
                    id="demo-input"
                    type="search"
                    placeholder="Search RCATS …"
                    value={searchValue}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <div className="spinner-border Typeahead-spinner" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <X className="close-search" onClick={removeFix} />
                </div>
                <div className="Typeahead-menu" id="search-outer">
                  <div className="header-search-suggestion custom-scrollbar">
                    {results.map((data) => (
                      <div className="ProfileCard u-cf" key={data.path}>
                        <div className="ProfileCard-details">
                          <div className="ProfileCard-realName">
                            <Link
                              to={data.path}
                              className="realname w-100 d-flex justify-content-start gap-2"
                              onClick={removeFix}
                            >
                              {data.icon ? (
                                <>
                                  <SvgIcon
                                    style={{ width: 16, height: 16 }}
                                    className="stroke-icon"
                                    iconId={`stroke-${data.icon}`}
                                  />
                                  <SvgIcon
                                    style={{ width: 16, height: 16 }}
                                    className="fill-icon"
                                    iconId={`fill-${data.icon}`}
                                  />
                                </>
                              ) : null}
                              {data.title}
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`Typeahead-menu empty-menu ${empty ? 'is-open' : ''}`}>
                  <div className="tt-dataset tt-dataset-0">
                    <div className="EmptyMessage">Opps!! There are no result found.</div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
          <Leftbar />
          <RightHeader />
        </Row>
      </div>
    </Fragment>
  );
}
